/**
 * Obstruction Road Detection Hook
 *
 * Thin wrapper over the shared `useRoadDetection` core. Reads the form anchor
 * point from the obstruction slice (`state.obstructions.formAnchorPoint`) and
 * delegates GPS detection + manual input for the country-aware
 * `RoadDisplayField` on the basic-info step.
 */
import { useGeoMarkupStore } from '@/store';
import { useRoadDetection } from '@/hooks/useRoadDetection';
import type { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import type { RoadSource } from '@/components/common/RoadDisplayField';

interface UseObstructionRoadDetectionProps {
  gpsLocationSuggestion: ReturnType<typeof useGPSLocationSuggestion>;
  setLocation: (value: string) => void;
}

interface UseObstructionRoadDetectionReturn {
  roadSource: RoadSource;
  handleRequestGPSDetection: () => void;
  handleManualRoadInput: (value: string) => void;
}

export function useObstructionRoadDetection({
  gpsLocationSuggestion,
  setLocation,
}: UseObstructionRoadDetectionProps): UseObstructionRoadDetectionReturn {
  const anchorLat = useGeoMarkupStore(
    (state) => state.obstructions.formAnchorPoint?.lat ?? null
  );
  const anchorLng = useGeoMarkupStore(
    (state) => state.obstructions.formAnchorPoint?.lng ?? null
  );

  return useRoadDetection({
    anchorLat,
    anchorLng,
    gpsLocationSuggestion,
    setLocation,
  });
}
